import { Alert, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { projectsApi, ProjectsApiError } from '../api/projectsApi';
import { projectSlice } from '../redux/ProjectSlice';
import { applicationSlice } from '../redux/ApplicationSlice';
import { Project } from '../Components/Projects/projects';
import { formatCodeInput, isValidShareCode } from '../utils/codeValidation';
import { ApplicationActions } from '../utils/constants';
import { parseProjectFromApiResponse } from '../utils/projectApiMetadata';
import { isBrowserOffline } from '../utils/networkStatus';

const ShareCodePage = () => {
  const { shareCode: shareCodeParam } = useParams<{ shareCode: string }>();
  const shareCode = shareCodeParam ? formatCodeInput(shareCodeParam) : '';
  const [status, setStatus] = useState<'loading' | 'error' | 'invalid'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [onlineRetryCount, setOnlineRetryCount] = useState(0);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const handleOnline = () => setOnlineRetryCount((count) => count + 1);

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => {
    if (!shareCodeParam) {
      setStatus('invalid');
      setErrorMessage(t('shareCodePage.error.missing', 'No share code in URL. Use a link like /share/ABC123.'));
      return;
    }

    if (!isValidShareCode(shareCode)) {
      setStatus('invalid');
      setErrorMessage(t('shareCodePage.error.invalid-format', 'Share code must be 6 uppercase letters and numbers (e.g., ABC123)'));
      return;
    }

    if (isBrowserOffline()) {
      setStatus('error');
      setErrorMessage(t('shareCodePage.error.offline', 'You appear to be offline. Connect to the internet to open share links.'));
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    let cancelled = false;

    const loadAndOpen = async () => {
      try {
        const response = await projectsApi.get(shareCode);
        if (cancelled) return;

        const isTemplate = response.is_template;

        if (isTemplate) {
          const { PROJECT_NAME } = ApplicationActions.Dialogs;
          dispatch(
            applicationSlice.actions.onOpenDialog({
              ...PROJECT_NAME,
              options: {
                title: 'projectNameDialog.new.title',
                rapidRobotCode: shareCode,
              },
            })
          );
          navigate('/', { replace: true });
        } else {
          const fetchedProject: Project = parseProjectFromApiResponse(response);
          const projectToLoad: Project = {
            ...fetchedProject,
            shareCode,
            accessLevel: 'viewer',
            readOnly: true,
            pin: undefined,
          };

          dispatch(
            projectSlice.actions.onSetAccessLevel({
              projectId: projectToLoad.id,
              level: 'viewer',
            })
          );
          dispatch(projectSlice.actions.onLoadProject(projectToLoad));
          navigate(`/editor/${projectToLoad.id}`, { replace: true });
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to open share code:', err);
        setStatus('error');
        if (err instanceof ProjectsApiError) {
          if (err.status === 404) {
            setErrorMessage(t('shareCodePage.error.not-found', 'Share code not found. Please check the link and try again.'));
          } else {
            setErrorMessage(t('shareCodePage.error.general', 'Failed to load project. Please check your connection and try again.'));
          }
        } else {
          setErrorMessage(t('shareCodePage.error.general', 'Failed to load project. Please check your connection and try again.'));
        }
      }
    };

    loadAndOpen();
    return () => {
      cancelled = true;
    };
  }, [shareCode, shareCodeParam, dispatch, navigate, t, onlineRetryCount]);

  if (status === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '50vh',
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography>{t('shareCodePage.loading', 'Loading project...')}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '50vh',
        px: 2,
      }}
    >
      <Stack spacing={2} alignItems="center" maxWidth="sm">
        <Alert severity="error">{errorMessage}</Alert>
        <Button variant="contained" onClick={() => navigate('/')}>
          {t('shareCodePage.button.go-home', 'Go to Projects')}
        </Button>
      </Stack>
    </Box>
  );
};

export default ShareCodePage;
